import type { Static } from "@sinclair/typebox";
import type { AccessKey } from "@volgo/core/contract";
import { type FormEvent, useState } from "react";
import { useSession } from "../../session";
import { Button } from "../../ui/Button";
import { ConfirmDialog } from "../../ui/Dialog";
import { describeError } from "../../ui/errors";
import { formatDateTime } from "../../ui/format";
import { Empty, ErrorNote, Loading } from "../../ui/States";
import { useLoad } from "../../ui/useLoad";

type Key = Static<typeof AccessKey>;

/** Access keys: guests log in with one. The key itself is shown once, right after it is made. */
export function AccessKeysPage() {
  const { client } = useSession();
  const list = useLoad(() => client.call("admin.listAccessKeys"), "access-keys");
  const [label, setLabel] = useState("");
  const [fresh, setFresh] = useState<{ label: string; key: string } | null>(null);
  const [revoking, setRevoking] = useState<Key | null>(null);
  const [error, setError] = useState<unknown>(null);

  function create(e: FormEvent) {
    e.preventDefault();
    const name = label.trim();
    if (!name) return;
    setError(null);
    client.call("admin.createAccessKey", { body: { label: name } }).then(
      (res) => {
        setFresh({ label: res.accessKey.label, key: res.key });
        setLabel("");
        list.reload();
      },
      (err: unknown) => setError(err),
    );
  }
  function revoke(key: Key) {
    setRevoking(null);
    setError(null);
    client.call("admin.revokeAccessKey", { params: { id: key.id } }).then(
      () => list.reload(),
      (err: unknown) => setError(err),
    );
  }

  return (
    <section aria-label="접근 키">
      <form className="inline-form" onSubmit={create}>
        <label className="field">
          누구에게 줄 키인지
          <input
            className="input"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            maxLength={100}
            placeholder="예: 친구 A"
          />
        </label>
        <Button type="submit" variant="primary" disabled={!label.trim()}>
          접근 키 만들기
        </Button>
      </form>
      {fresh && (
        <div className="notice" role="status">
          <b>“{fresh.label}” 키를 만들었습니다</b>
          <p>이 키는 지금만 볼 수 있습니다. 복사해서 전달하세요. 이 화면을 떠나면 다시 볼 수 없습니다.</p>
          <input
            className="input key-once"
            readOnly
            aria-label="새 접근 키"
            value={fresh.key}
            onFocus={(e) => e.target.select()}
          />
        </div>
      )}
      {error !== null && (
        <p className="note note-warn" role="alert">
          {describeError(error, { NOT_FOUND: "이미 없는 키입니다. 목록을 새로 고치세요." })}
        </p>
      )}

      {list.status === "loading" && <Loading />}
      {list.status === "error" && <ErrorNote error={list.error} onRetry={list.reload} />}
      {list.status === "ready" && list.data.items.length === 0 && (
        <Empty title="접근 키가 없습니다">
          <p>위에서 이름을 적고 키를 만드세요.</p>
        </Empty>
      )}
      {list.status === "ready" && list.data.items.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">이름</th>
                <th scope="col">키 앞부분</th>
                <th scope="col">만든 때</th>
                <th scope="col">마지막 사용</th>
                <th scope="col">상태</th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((key) => (
                <tr key={key.id}>
                  <th scope="row">{key.label}</th>
                  <td>{key.prefix}…</td>
                  <td>{formatDateTime(key.createdAt)}</td>
                  <td>{key.lastUsedAt ? formatDateTime(key.lastUsedAt) : "—"}</td>
                  <td>
                    {key.revokedAt ? (
                      <span className="note">{formatDateTime(key.revokedAt)} 폐기됨</span>
                    ) : (
                      <Button aria-label={`${key.label} 키 폐기`} onClick={() => setRevoking(key)}>
                        키 폐기
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {revoking && (
        <ConfirmDialog
          title={`“${revoking.label}” 키를 폐기할까요?`}
          confirmLabel="폐기"
          onConfirm={() => revoke(revoking)}
          onCancel={() => setRevoking(null)}
        >
          <p>
            이 키로는 더 로그인할 수 없고, 이 키로 로그인한 사람은 바로 로그아웃됩니다. 되돌릴 수 없습니다.
          </p>
        </ConfirmDialog>
      )}
    </section>
  );
}
